export interface WidgetLeadData {
  firstName?: string
  lastName?: string
  middleName?: string
  email?: string
  phone?: string
  address?: string
  addressLine1?: string
  addressLine2?: string
  city?: string
  state?: string
  zipCode?: string
  about?: string
  referredBy?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
}

const LEGACY_WIDGET_STORAGE_KEY = 'widget'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const cleanString = (value: unknown) => {
  if (typeof value !== 'string') return undefined

  const trimmed = value.trim()
  return trimmed || undefined
}

const readRawLocalStorageValue = (key: string) => {
  if (typeof window === 'undefined') return undefined

  try {
    return cleanString(window.localStorage.getItem(key))
  } catch {
    return undefined
  }
}

const parseJsonRecord = (value: string) => {
  try {
    const parsed = JSON.parse(value)
    return isRecord(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

const readEncodedWidgetState = () => {
  const raw = readRawLocalStorageValue(LEGACY_WIDGET_STORAGE_KEY)
  if (!raw) return {}

  if (raw.startsWith('{')) {
    return parseJsonRecord(raw)
  }

  if (typeof window === 'undefined') return {}

  const encoded = raw.startsWith('=') ? raw.slice(1) : raw

  try {
    return parseJsonRecord(window.atob(encoded))
  } catch {
    return {}
  }
}

const readRecordString = (record: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = cleanString(record[key])
    if (value) return value
  }

  return undefined
}

const readDirectString = (keys: string[]) => {
  for (const key of keys) {
    const value = readRawLocalStorageValue(key)
    if (value) return value
  }

  return undefined
}

const readLeadField = (
  source: Record<string, unknown>,
  keys: string[]
) => readRecordString(source, keys) ?? readDirectString(keys)

export const readWidgetLeadData = (): WidgetLeadData => {
  const source = readEncodedWidgetState()

  return {
    firstName: readLeadField(source, ['firstName', 'first_name']),
    lastName: readLeadField(source, ['lastName', 'last_name']),
    middleName: readLeadField(source, ['middleName', 'middle_name']),
    email: readLeadField(source, ['email']),
    phone: readLeadField(source, ['phone', 'phoneNumber', 'phone_number']),
    address: readLeadField(source, ['address', 'fullAddress', 'full_address']),
    addressLine1: readLeadField(
      source,
      ['addressLine1', 'address_line_1', 'street']
    ),
    addressLine2: readLeadField(source, ['addressLine2', 'address_line_2']),
    city: readLeadField(source, ['city']),
    state: readLeadField(source, ['state']),
    zipCode: readLeadField(
      source,
      ['zipCode', 'zip_code', 'zip', 'postalCode']
    ),
    about: readLeadField(source, ['about', 'msg', 'message']),
    referredBy: readLeadField(
      source,
      ['referredBy', 'referred_by', 'referralName']
    ),
    utmSource: readLeadField(source, ['utmSource', 'utm_source', 'source']),
    utmMedium: readLeadField(source, ['utmMedium', 'utm_medium']),
    utmCampaign: readLeadField(
      source,
      ['utmCampaign', 'utm_campaign', 'dealername']
    ),
    utmTerm: readLeadField(source, ['utmTerm', 'utm_term']),
    utmContent: readLeadField(source, ['utmContent', 'utm_content'])
  }
}

export const hasWidgetLeadData = () => {
  const data = readWidgetLeadData()
  return Object.values(data).some(Boolean)
}
